import { useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Divider,
  Chip,
  Box,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  useFetchTicketEstados,
  useFetchTicketPrioridades,
  useFetchTicketAreas,
  useFetchTicketCanales,
  useFetchTicketTipos,
  useFetchTicketCategorias,
  useFetchTicketSubcategorias,
  useCreateGestionTicket,
  useFetchMyTicketArea,
  useFetchUsuarioResumen,
  useEliminarTicket,
  useAgregarComentario,
} from '@/shared/actions/erp/tickets-soporte-cliente';
import { useSlaEnabled } from '@/shared/hooks/erp/tickets/useSlaEnabled';

import type {
  IVision360Cliente,
  IVision360Contrato,
  ITicketCreate,
  IVision360TicketResumen,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { OrdenServicioSaveForm, useCreateOrdenServicio, useCreateTicketSoporteCorporativo } from '@/shared/actions/erp';
import { IVisitaForm } from '@/shared/interfaces/erp/visita/visita.interface';
import OrdenServico from '../OrdenServicio/OrdenServicio';
import { toast } from 'react-toastify';
import { useAuthStore } from '@/store/auth';
import { hasSystemModule } from '@/shared/utils';
import { SystemModulesEnum } from '@/auth/interfaces/system-modules.enum';
import useSelectDateTimerPreScheduling from '@/erp/sale/component/calendario-preagendamiento-venta/useSelectDateTimerPreScheduling.store';
import { toHour } from '@/shared';
import dayjs from 'dayjs';

interface FormCrearTicketProps {
  open: boolean;
  onClose: () => void;
  cliente: IVision360Cliente;
  contrato: IVision360Contrato | null;
  hasMasivoActivo: boolean;
  /** Abre el modal con "¿Requiere Visita?" ya activado (flujo del boton "+"
      del detalle de un ticket existente que no nacio como visita). */
  defaultRequiereVisita?: boolean;
  /** Ticket desde el que se abrio este modal: cuando la visita se crea con
      exito se le agrega un comentario de referencia (codigo del ticket
      nuevo, quien lo creo y a que hora). */
  ticketOrigen?: { id: string; codigo?: string } | null;
  tickets?: IVision360TicketResumen[] | null;
}

interface FormValues {
  tipo: string;
  categoria: string;
  subcategoria: string;
  prioridad: string;
  canal: string;
  area_origen: string;
  area_destino: string;
  descripcion: string;
  contacto_nombre: string;
  contacto_tipo: string;
  contacto_telefono: string;
  requiere_visita: boolean;
  es_ticket_rapido: boolean;
  tipo_orden_servicio: string | null;
  // Fecha/hora en que el cliente experimento la afectacion. Input
  // datetime-local (formato "YYYY-MM-DDTHH:mm"). Opcional.
  fecha_afectacion: string;
  visita: IVisitaForm;
  contrato_id?: string;

  // agrega estos si los usas en onSubmit:
  id_zona?: string;
  pais?: string;
  provincia?: string;
  ciudad?: string;
  parroquia?: string;
  zona?: string;
  direccion?: string;
  observaciones?: string;
  fecha_visita?: string;
}

export default function FormCrearTicket({
  open,
  onClose,
  cliente,
  contrato,
  hasMasivoActivo,
  defaultRequiereVisita = false,
  ticketOrigen = null,
  tickets = null
}: FormCrearTicketProps) {

  const navigate = useNavigate();
  //***obtener del store 
  const selectFloat = useSelectDateTimerPreScheduling(s => s.selectFloat)
  const selectDate = useSelectDateTimerPreScheduling(s => s.selectDate)
  const slot = useSelectDateTimerPreScheduling(s => s.slot)
  const setIsSave = useSelectDateTimerPreScheduling(s => s.setIsSave)
  const stopAndResetTemporizer = useSelectDateTimerPreScheduling(s => s.stopAndResetTemporizer)
  // El area de origen se toma siempre del usuario logueado. El backend
  // expone /catalogo/me/area resolviendo via UsuarioArea (X-User-Id)
  // y devolviendo {id_area, nombre, codigo}. El campo queda read-only.
  const user = useAuthStore(s => s.user);
  const isSuperUser = !!(user as any)?.is_superuser;
  const currentUserId = (user as any)?.user_id || (user as any)?.user || 0;
  const { data: _resumenRes } = useFetchUsuarioResumen(currentUserId, !!currentUserId);
  const _r = _resumenRes as any;
  const userPermisos: string[] =
    _r?.data?.data?.permisos || _r?.data?.permisos || _r?.permisos || [];
  // Guard del flow Orden de Servicio (visita/traslado). Si el usuario no
  // tiene el permiso, el checkbox "¿Requiere Visita?" y el bloque <OrdenServico>
  // se ocultan. Retrocompatible: si nadie tiene permisos del modulo
  // 'tickets.gestionar_orden_servicio' aun (migracion V1_12_0 no aplicada),
  // mostramos el bloque para no romper la UI en ambientes antiguos.
  //
  // Ademas, el modulo de sistema 'VISITAS.REQUIERE_VISITA' (activable desde
  // Gestion de Accesos > Modulos) tambien habilita este bloque, para controlar
  // el flujo de visitas por rol de forma consistente con el resto de eslabones.
  const tieneAlgunPermisoOS = userPermisos.includes('tickets.gestionar_orden_servicio')
    || userPermisos.some(p => p.startsWith('tickets.'));
  const canGestionarOrdenServicio =
    isSuperUser
    || !tieneAlgunPermisoOS
    || userPermisos.includes('tickets.gestionar_orden_servicio')
    || hasSystemModule(SystemModulesEnum.VISITAS_REQUIERE_VISITA);
  const { data: myAreaRes } = useFetchMyTicketArea();
  const myArea = (myAreaRes?.data as any)?.data ?? (myAreaRes?.data as any) ?? null;
  const userAreaDefault: string = myArea?.id_area ?? '';
  const userAreaNombre: string = myArea?.nombre ?? '';

  const form = useForm<FormValues>({
    defaultValues: {
      tipo: '',
      categoria: '',
      subcategoria: '',
      prioridad: '',
      canal: '',
      area_origen: '',
      area_destino: '',
      descripcion: '',
      contacto_nombre: cliente.razon_social,
      contacto_tipo: 'titular',
      contacto_telefono: cliente.celular,
      requiere_visita: !!defaultRequiereVisita,
      es_ticket_rapido: false,
      fecha_afectacion: '',
      tipo_orden_servicio: null,
      //
      id_zona: "",
      pais: "",
      provincia: "",
      ciudad: "",
      parroquia: "",
      zona: "",
      direccion: "",
      observaciones: "",
      fecha_visita: "",
      contrato_id: ''
    },
  });

  const { control, handleSubmit, watch, setValue, reset, formState: { errors } } = form

  const watchTipo = watch('tipo');
  const watchCategoria = watch('categoria');
  const watchAreaDestino = watch('area_destino');

  const { slaEnabled } = useSlaEnabled();

  // Catalogs
  const { data: estadosRes } = useFetchTicketEstados();
  const { data: prioridadesRes } = useFetchTicketPrioridades();
  const { data: areasRes } = useFetchTicketAreas();
  const { data: canalesRes } = useFetchTicketCanales();
  // Tipos filtrados por area destino. Si no hay area destino seleccionada,
  // el query queda deshabilitado y se devuelve lista vacia (forza al user
  // a elegir area destino primero). El backend devuelve los tipos del area
  // + los legacy (area_id NULL).
  const { data: tiposRes } = useFetchTicketTipos({
    areaId: watchAreaDestino || undefined,
    enabled: !!watchAreaDestino,
  });
  const { data: categoriasRes } = useFetchTicketCategorias(watchTipo || undefined);
  const { data: subcategoriasRes } = useFetchTicketSubcategorias(watchCategoria || undefined);

  // Helper: los endpoints de catalogos pueden devolver T[] directo o un
  // wrapper { data: T[], count: number }. Esta funcion normaliza ambos
  // casos a T[] sin que TypeScript se queje de '.data' no existiendo en
  // un array.
  const toArray = <T,>(res: any): T[] => {
    const body = res?.data;
    if (!body) return [];
    if (Array.isArray(body)) return body;
    return body.data ?? [];
  };

  const estados = useMemo(() => toArray<any>(estadosRes), [estadosRes]);
  const prioridades = useMemo(() => toArray<any>(prioridadesRes), [prioridadesRes]);
  const areas = useMemo(() => toArray<any>(areasRes), [areasRes]);
  const canales = useMemo(() => toArray<any>(canalesRes), [canalesRes]);
  const tipos = useMemo(() => toArray<any>(tiposRes), [tiposRes]);
  const categorias = useMemo(() => toArray<any>(categoriasRes), [categoriasRes]);
  const subcategorias = useMemo(() => toArray<any>(subcategoriasRes), [subcategoriasRes]);

  const estadoInicial = useMemo(() => estados.find((e: any) => e.es_inicial), [estados]);
  //mutate
  const eliminarTicketMutation = useEliminarTicket();

  // Reset category when tipo changes
  useEffect(() => {
    setValue('categoria', '');
    setValue('subcategoria', '');
  }, [watchTipo, setValue]);

  useEffect(() => {
    setValue('subcategoria', '');
  }, [watchCategoria, setValue]);

  // Cuando cambia el area destino, el arbol tipo/categoria/subcategoria
  // queda invalido: la cascade es Area -> Tipo -> Categoria -> Subcategoria.
  // Reseteamos para que el user elija de nuevo desde el tipo.
  useEffect(() => {
    setValue('tipo', '');
    setValue('categoria', '');
    setValue('subcategoria', '');
  }, [watchAreaDestino, setValue]);

  // Reset form when dialog opens. area_origen se precarga con el
  // departamento del usuario logueado (read-only en el UI).
  useEffect(() => {
    if (open) {
      reset({
        tipo: '',
        categoria: '',
        subcategoria: '',
        prioridad: '',
        canal: '',
        area_origen: userAreaDefault,
        area_destino: '',
        descripcion: '',
        contacto_nombre: cliente.razon_social,
        contacto_tipo: 'titular',
        contacto_telefono: cliente.celular,
        requiere_visita: !!defaultRequiereVisita,
        es_ticket_rapido: false,
        fecha_afectacion: '',
        fecha_visita: '',
        contrato_id: '',
      });
    }
  }, [open, cliente, reset, userAreaDefault, defaultRequiereVisita]);

  // Reset store del planificador cuando se cierra el modal
  useEffect(() => {
    if (!open) {
      stopAndResetTemporizer();
    }
  }, [open]);

  // Cuando el ticket es rapido, el area de origen se "elimina": en DB
  // queda null y el ticket pertenece al area del propio creador.
  const watchEsRapido = watch('es_ticket_rapido');
  useEffect(() => {
    if (watchEsRapido) {
      setValue('area_origen', '');
      // Ticket rapido: el area destino se fija sola con el area del propio
      // creador (la misma de "Area Origen (tu area)") y queda read-only en
      // el UI. Ademas un ticket rapido nunca lleva visita: se apaga el flag
      // por si estaba marcado antes de activar el check.
      setValue('area_destino', userAreaDefault);
      setValue('requiere_visita', false);
    } else {
      setValue('area_origen', userAreaDefault);
    }
  }, [watchEsRapido, userAreaDefault, setValue]);

  const createTicketMutation = useCreateGestionTicket({
    navigate,
    enableRedirect: false,
    returnUrl: '',
    enableSuccessAlert: true,
    customSuccessMessage: 'Ticket creado exitosamente',
  });
  //mutation visita
  const createOrdenServicioMutation = useCreateOrdenServicio({
    navigate,
    //returnUrl
  })

  // Flujo "+" (crear visita desde un ticket existente): al terminar de
  // crear el ticket de visita se deja un comentario de referencia en el
  // ticket de ORIGEN con el codigo del ticket nuevo, quien lo creo y a que
  // hora. useAgregarComentario ya refetchea el historial del ticket de
  // origen, asi que el comentario aparece en vivo detras de este modal.
  const comentarTicketOrigenMutation = useAgregarComentario();
  const comentarVisitaEnTicketOrigen = (ticketNuevo: any) => {
    if (!ticketOrigen?.id) return;
    const codigoNuevo = ticketNuevo?.codigo_ticket || ticketNuevo?.id_ticket || '';
    const quien = (user as any)?.username || (user as any)?.email || 'usuario';
    const cuando = dayjs().format('DD/MM/YYYY HH:mm');
    comentarTicketOrigenMutation.mutate({
      id: ticketOrigen.id,
      data: {
        comentario: `Se creó una visita con el ticket ${codigoNuevo} — creada por ${quien} el ${cuando}.`,
      },
    });
  };

  const useCreateTicketSoporteCorporativoMutation = useCreateTicketSoporteCorporativo({
    navigate
  });

  const onSubmit = (values: FormValues) => {

    console.log('Form values on submit:', values);

    // validadores de ip adicionales
    const subcategoriaSeleccionada = subcategorias.find(
      (s: any) => s.id_ticket_subcategoria === values.subcategoria
    );

    // si ya tiene un ticket por solicitud de ip, no crear otro
    if (
      subcategoriaSeleccionada?.nombre.toUpperCase() === 'SOLICITAR IP PUBLICA' &&
      tickets?.some((t: any) => t.subcategoria.toUpperCase() === 'SOLICITAR IP PUBLICA' && t.estado.toUpperCase() !== 'CERRADO')
    ) {
      toast.error('Ya existe un ticket abierto para SOLICITAR IP PÚBLICA. No se puede crear otro.');
      return false;
    }

    if (
      subcategoriaSeleccionada?.nombre.toUpperCase() === 'SOLICITAR IP PUBLICA' &&
      contrato?.estado_servicio !== 'Servicio Activo'
    ) {
      toast.error('El contrato debe esta Activo para solicitar una IP Pública.');
      return false;
    }
    if (
      subcategoriaSeleccionada?.nombre.toUpperCase() === 'SOLICITAR IP PUBLICA' &&
      contrato?.datos_tecnicos?.ipv4_publica != ''
    ) {
      toast.error('El contrato ya tiene IP Pública asignada. No se puede solicitar otra.');
      return false;
    }
    if (
      subcategoriaSeleccionada?.nombre.toUpperCase() === 'CANCELAR IP PUBLICA' &&
      contrato?.datos_tecnicos?.ipv4_publica == ''
    ) {
      toast.error('El contrato no tiene IP Pública asignada. No se puede cancelar.');
      return false;
    }



    // Ticket rapido: solo cambia que area_origen=null (el ticket nace
    // sin "area de origen" del creador). El area_destino + arbol
    // Tipo/Cat/Subcat se eligen igual que en un ticket normal — el flag
    // es_ticket_rapido ya etiqueta el ticket para el backend.
    console.log("descipción", values.descripcion)
    if (!values.descripcion) return toast.error("Descripción del Problema es campo obligatorio. ");
    const esRapido = !!values.es_ticket_rapido;
    const areaOrigen = esRapido ? null : (values.area_origen || null);
    const areaDestino = values.area_destino || values.area_origen || null;

    const payload: ITicketCreate = {
      cliente_id: undefined,
      contrato_id: undefined,
      cliente_identificacion: cliente.identificacion,
      cliente_razon_social: cliente.razon_social,
      tipo: values.tipo,
      categoria: values.categoria || null,
      subcategoria: values.subcategoria || null,
      estado_ticket: estadoInicial?.id_ticket_estado || '',
      prioridad: values.prioridad,
      canal: values.canal || null,
      area_origen: areaOrigen,
      area_destino: areaDestino,
      descripcion: values.descripcion,
      contacto_nombre: values.contacto_nombre,
      contacto_tipo: values.contacto_tipo,
      contacto_telefono: values.contacto_telefono,
      requiere_visita: values.requiere_visita,
      es_ticket_rapido: esRapido,
      es_masivo: false,
      // datetime-local -> ISO. Si no llenaron, null (el backend acepta null).
      fecha_afectacion: values.fecha_afectacion
        ? new Date(values.fecha_afectacion).toISOString()
        : null,
    };

    //console.log(form.getValues().contrato_id)
    // Add client/contract references from Vision360
    if (cliente.id_cliente) {
      (payload as any).cliente = cliente.id_cliente;
    }
    if (contrato?.id_contrato) {
      (payload as any).contrato = contrato.id_contrato;
    }
    //return

    // El ticket SIEMPRE se crea. Antes el mutate estaba dentro de
    // if (requiere_visita), lo que provocaba que un Ticket Rapido (sin
    // visita) no se creara nunca: el flag "Ticket Rapido" solo cambia
    // area_origen=null, NO implica requiere_visita.
    // Dentro del onSuccess se decide condicionalmente si encadenar la
    // orden de servicio (solo cuando el usuario marcó requiere_visita=SI
    // y tipo_orden_servicio=VISITA).
    setIsSave(true);
    createTicketMutation.mutate(payload, {
      onSuccess: (ticketResponse: any) => {
        const ticket = ticketResponse?.data;
        const idTicket = ticket?.id_ticket;
        if (!idTicket) {
          toast.error("No vino id_ticket en la respuesta del backend");
          return;
        }

        const necesitaOrdenServicio =
          !!values.requiere_visita && values.tipo_orden_servicio === "VISITA";

        if (!necesitaOrdenServicio) {
          stopAndResetTemporizer();
          onClose();
          return;
        }

        //const contratoOrdenServicio = contrato?.id_contrato || values.contrato_id || '';
        const ordenServicioPayload: OrdenServicioSaveForm = {
          id_ticket: idTicket,
          tipo_orden_servicio: "VISITA",
          user_create: user?.username!,
          contrato: contrato?.numero_referencia!,
          //contrato: values.contrato_id || contratoOrdenServicio,
          id_instalacion_externa: contrato?.id_instalacion || -9999999,
          visita: {
            id_cliente: cliente.id_cliente,
            area_destino_id: values.area_destino,
            id_zona: values.id_zona,
            pais: values.pais,
            provincia: values.provincia,
            ciudad: values.ciudad,
            parroquia: values.parroquia,
            zona: values.zona,
            direccion: values.direccion,
            observaciones: values.observaciones,
            flota: selectFloat,
            fecha_visita: selectDate && slot?.hora ? `${selectDate?.format("YYYY-MM-DD")}T${toHour(slot.hora).padStart(5,'0')}:00` : values.fecha_visita,
            //fecha_visita:selectDate && slot?.hora ? `${selectDate?.format("YYYY-MM-DD")}T${slot.hora}:00`: values.fecha_visita,
            //fecha_visita: values.fecha_visita,
            // "Otros conceptos" (equipos + método de pago) → el micro los reenvía
            // a Django (agendamiento.observaciones.otros_conceptos) para el cobro
            // a Softnet al finalizar. id_contrato_softnet = numero_referencia del
            // contrato (= idContrato de Softnet que también viaja en `contrato`).
            otros_conceptos: (values as any).otros_conceptos || [],
            tipo_instalacion: (values as any).tipo_instalacion || null,
            id_contrato_softnet: contrato?.numero_referencia,
          } as any
        };
        console.log('visita', ordenServicioPayload)
        createOrdenServicioMutation.mutate(ordenServicioPayload, {
          onSuccess: () => {
            comentarVisitaEnTicketOrigen(ticket);
            stopAndResetTemporizer();
            onClose();
          },
          onError: () => {
            eliminarTicketMutation.mutate(idTicket); // compensación cuando falla con visita
            toast.error('La visita no pudo crearse, el ticket será eliminado')
          }
        });
      },
    });



  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>Crear Nuevo Ticket</Typography>
        <Typography variant="body2" color="text.secondary">
          Cliente: {cliente.razon_social} | {cliente.identificacion}
          {contrato && ` | Contrato: ${contrato.codigo}`}
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit, (errors) => {
        console.log(errors)
        toast.error("Revise los campos requeridos.")
      })}
      >
        <DialogContent dividers>
          {hasMasivoActivo && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Este cliente tiene un daño masivo activo. Considere vincular el ticket al masivo existente.
            </Alert>
          )}

          {/* Clasificación. Orden: Area Destino -> Tipo -> Categoria ->
              Subcategoria. El area destino se elige PRIMERO porque define
              el subconjunto de tipos disponibles (los tipos pertenecen a
              un area; los tipos legacy con area=NULL son visibles para
              todas las areas). */}
          <Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
            Clasificación del Ticket
          </Typography>
          <Grid container spacing={2}>
            {/* Area Destino — primero. En ticket rapido el destino se
                fija solo: es el area del propio creador (la misma de "Area
                Origen (tu area)") y se muestra read-only; el valor real
                (uuid) lo setea el effect de watchEsRapido en area_destino,
                asi el arbol Tipo/Cat/Subcat carga con esa area. */}
            <Grid item xs={12} md={4}>
              {watchEsRapido ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Área Destino *"
                  value={userAreaNombre || '-'}
                  disabled
                  InputProps={{ readOnly: true }}
                  helperText="Tomada del usuario logueado"
                />
              ) : (
                <Controller
                  name="area_destino"
                  control={control}
                  rules={{ required: 'Área destino es requerida' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      size="small"
                      label="Área Destino *"
                      error={!!errors.area_destino}
                      helperText={errors.area_destino?.message || 'Define el árbol de Tipo/Categoría'}
                    >
                      <MenuItem value="">-- Seleccionar --</MenuItem>
                      {areas.map((a: any) => (
                        <MenuItem key={a.id_area} value={a.id_area}>{a.nombre}</MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="tipo"
                control={control}
                rules={{ required: 'Tipo es requerido' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Tipo *"
                    disabled={!watchAreaDestino}
                    error={!!errors.tipo}
                    helperText={
                      errors.tipo?.message ||
                      (!watchAreaDestino
                        ? 'Seleccione área destino primero'
                        : undefined)
                    }
                  >
                    {tipos.map((t: any) => (
                      <MenuItem key={t.id_ticket_tipo} value={t.id_ticket_tipo}>
                        {t.nombre}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="categoria"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Categoría"
                    disabled={!watchTipo}
                  >
                    <MenuItem value="">-- Seleccionar --</MenuItem>
                    {categorias.map((c: any) => (
                      <MenuItem key={c.id_ticket_categoria} value={c.id_ticket_categoria}>
                        {c.nombre}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="subcategoria"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Subcategoría"
                    disabled={!watchCategoria}
                  >
                    <MenuItem value="">-- Seleccionar --</MenuItem>
                    {subcategorias.map((s: any) => (
                      <MenuItem key={s.id_ticket_subcategoria} value={s.id_ticket_subcategoria}>
                        {s.nombre}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Prioridad, Canal, Áreas */}
          <Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
            Prioridad y Asignación
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Controller
                name="prioridad"
                control={control}
                rules={{ required: 'Prioridad es requerida' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Prioridad *"
                    error={!!errors.prioridad}
                    helperText={errors.prioridad?.message}
                  >
                    {prioridades.map((p: any) => (
                      <MenuItem key={p.id_ticket_prioridad} value={p.id_ticket_prioridad}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: p.color || '#9e9e9e' }} />
                          {p.nombre}
                          {slaEnabled && (
                            <Chip label={`SLA: ${Math.round(p.sla_minutos / 60)}h`} size="small" variant="outlined" />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="canal"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Canal">
                    <MenuItem value="">-- Seleccionar --</MenuItem>
                    {canales.map((c: any) => (
                      <MenuItem key={c.id_ticket_canal} value={c.id_ticket_canal}>{c.nombre}</MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            {/* Area de origen: se muestra el NOMBRE del area del usuario
                logueado (read-only). El UUID va en area_origen del payload
                y el backend lo valida; si no cuadra, el backend lo resuelve
                solo via UsuarioArea. Campo oculto cuando es ticket rapido. */}
            {!watchEsRapido && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Área Origen (tu área)"
                  value={userAreaNombre || '-'}
                  disabled
                  InputProps={{ readOnly: true }}
                  helperText="Tomada del usuario logueado"
                />
              </Grid>
            )}
            {/* Fecha y hora de afectacion + Ticket Rapido (checkbox al lado).
                fecha_afectacion (LocalDateTime). Opcional. */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Controller
                  name="fecha_afectacion"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="datetime-local"
                      fullWidth
                      size="small"
                      label="Fecha y hora de afectación"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
                <Controller
                  name="es_ticket_rapido"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ ml: 0, mr: 0, whiteSpace: 'nowrap' }}
                      control={
                        <Checkbox
                          size="small"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Ticket Rápido"
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Descripción */}
          <Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
            Descripción
          </Typography>
          <Controller
            name="descripcion"
            control={control}
            rules={{ required: 'La descripción es requerida', minLength: { value: 10, message: 'Mínimo 10 caracteres' } }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                rows={3}
                size="small"
                label="Descripción del problema *"
                error={!!errors.descripcion}
                helperText={errors.descripcion?.message}
              />
            )}
          />

          <Divider sx={{ my: 2 }} />

          {/* Contacto */}
          <Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
            Datos de Contacto
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Controller
                name="contacto_nombre"
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Nombre Contacto" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="contacto_tipo"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Tipo Contacto">
                    <MenuItem value="titular">Titular</MenuItem>
                    <MenuItem value="autorizado">Autorizado</MenuItem>
                    <MenuItem value="familiar">Familiar</MenuItem>
                    <MenuItem value="otro">Otro</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="contacto_telefono"
                control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Teléfono Contacto" />
                )}
              />
            </Grid>
          </Grid>

          {/* Options. Ticket Rapido se movio al lado de Fecha de afectacion
              (checkbox) — aqui solo queda ¿Requiere Visita? que dispara la
              UI de Orden de Servicio. Solo visible si el usuario tiene
              permiso tickets.gestionar_orden_servicio. Oculto tambien
              cuando es Ticket Rapido: un rapido nunca lleva visita (el
              effect de watchEsRapido ademas apaga el flag). */}
          {canGestionarOrdenServicio && !watchEsRapido && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="requiere_visita"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="¿Requiere Visita?"
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}

          {/* Orden de Servicio/Traslado */}

          {canGestionarOrdenServicio && !watchEsRapido && <Divider sx={{ my: 2 }} />}

          {canGestionarOrdenServicio && !watchEsRapido && (
            <OrdenServico
              key={'orden-traslado'}
              form={form}
              cliente={cliente}
            />
          )}

        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={createTicketMutation.isPending}
          >
            {createTicketMutation.isPending ? 'Creando...' : 'Crear Ticket'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
